import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicShell from "@/layouts/PublicShell";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Users, TrendingUp, Megaphone, MapPin, Wrench, Package, Globe,
  Sparkles, ArrowUpRight, ChevronRight, FileText, Download, Award,
  CheckCircle2, Rocket, Compass, BookOpen,
} from "lucide-react";

/**
 * /partners — Mirrorloops Global Partner Network landing page.
 *
 * Phase A scope: hero + 6-category grid + 4-tier ladder + benefits +
 * FAQ + Founding 100 block + book downloads + apply CTA. The
 * category/tier/FAQ catalog ships from the backend /partners/meta
 * endpoint so admins can tune copy without a redeploy later.
 */

const CATEGORY_ICONS = {
  sales: TrendingUp,
  advertising: Megaphone,
  location: MapPin,
  technology: Wrench,
  distribution: Package,
  regional: Globe,
};

const TIER_TONES = {
  registered: "border-white/20 bg-white/[0.03] text-white/70",
  silver: "border-slate-300/40 bg-slate-200/[0.04] text-slate-100",
  gold: "border-[#D4B878]/50 bg-[#D4B878]/[0.08] text-[#E8D6A3]",
  platinum: "border-[#CCFF00]/50 bg-[#CCFF00]/[0.06] text-[#CCFF00]",
};

export default function Partners() {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api
      .get("/partners/meta")
      .then((r) => setMeta(r.data))
      .catch(() => setMeta({}));
  }, []);

  const categories = meta?.categories || [];
  const tiers = meta?.tiers || [];
  const faqs = meta?.faqs || [];
  const stats = meta?.stats || {};
  const book = meta?.knowledge_book || [];

  return (
    <PublicShell>
      <SEO
        title="Partners — Build Your Business With Mirrorloops"
        description="Join a global network of professionals building the future of advertising, digital signage, and commercial displays. Six ways to partner, four tiers of recognition, one transparent commercial platform."
        path="/partners"
      />

      {/* HERO */}
      <section
        data-testid="partners-hero"
        className="relative pt-36 pb-24 overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-1/4 w-[500px] h-[500px] rounded-full bg-[#D4B878]/[0.06] blur-[120px]" />
          <div className="absolute bottom-10 right-1/4 w-[400px] h-[400px] rounded-full bg-[#CCFF00]/[0.04] blur-[100px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-5 flex items-center gap-2">
            <Sparkles size={12} /> {t("partners.heroEyebrow")}
          </div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-semibold text-white tracking-tight leading-[1.05]">
            {t("partners.heroTitle")}
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-white/60 max-w-2xl leading-relaxed">
            {t("partners.heroSub")}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/partners/apply"
              data-testid="partners-cta-apply"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold px-6 py-3 text-sm"
            >
              {t("partners.applyCta")} <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/partners/directory"
              data-testid="partners-cta-directory"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 text-white hover:bg-white/5 px-6 py-3 text-sm"
            >
              {t("partners.directoryCta")} <Compass size={14} />
            </Link>
          </div>

          {/* Honest counters */}
          <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
            {[
              { k: "active_partners", label: "Active partners" },
              { k: "applications_submitted", label: "Applications in" },
              { k: "public_directory_count", label: "Public profiles" },
            ].map((s) => (
              <div key={s.k}>
                <div
                  data-testid={`partners-stat-${s.k}`}
                  className="text-3xl font-semibold text-white font-mono"
                >
                  {(stats[s.k] || 0).toLocaleString()}
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] font-mono text-white/40 uppercase tracking-[0.16em]">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-[#CCFF00]/30 text-[#CCFF00]">
              Observed
            </span>{" "}
            · Values are live from the applications table — not marketing.
          </div>
        </div>
      </section>

      {/* 6 CATEGORIES */}
      <section
        data-testid="partners-categories"
        className="max-w-6xl mx-auto px-6 py-16 border-t border-white/10"
      >
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2">
            Six ways to partner
          </div>
          <h2 className="text-3xl sm:text-5xl font-semibold text-white tracking-tight">
            Pick the lane that fits your business.
          </h2>
          <p className="mt-4 text-white/60 max-w-2xl">
            Each path has its own commercial model, support tooling, and
            onboarding — you can start in one and add more as you grow.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((c) => {
            const Icon = CATEGORY_ICONS[c.id] || Users;
            return (
              <div
                key={c.id}
                data-testid={`partners-category-${c.id}`}
                className="border border-white/10 rounded-lg p-5 bg-white/[0.02] hover:border-[#D4B878]/40 transition-all"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded border border-[#D4B878]/30 bg-[#D4B878]/[0.08] flex items-center justify-center text-[#E8D6A3]">
                    <Icon size={16} />
                  </div>
                  <div className="text-white font-semibold">{c.label}</div>
                </div>
                <div className="text-sm text-white/60 mb-3 leading-relaxed">
                  {c.tagline}
                </div>
                <div className="text-xs text-white/50 leading-relaxed mb-4">
                  {c.blurb}
                </div>
                <div className="pt-3 border-t border-white/10">
                  <div className="text-[9px] uppercase tracking-[0.22em] text-[#D4B878] mb-1 font-mono">
                    Default commission
                  </div>
                  <div className="text-[11px] text-white/70 leading-relaxed">
                    {c.default_commission}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* TIER LADDER */}
      <section
        data-testid="partners-tiers"
        className="max-w-6xl mx-auto px-6 py-16 border-t border-white/10"
      >
        <div className="mb-10 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
              <Award size={12} /> Four tiers of recognition
            </div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">
              Grow with the network.
            </h2>
            <p className="mt-4 text-white/60 leading-relaxed">
              Tier is based on verified eligible revenue, service quality,
              and compliance — never on recruitment. Higher tiers unlock
              priority support, enterprise opportunities, and strategic
              account access.
            </p>
          </div>
          <div className="lg:col-span-3 space-y-3">
            {tiers.map((t) => (
              <div
                key={t.id}
                data-testid={`partners-tier-${t.id}`}
                className={`border rounded-lg p-4 ${TIER_TONES[t.id] || TIER_TONES.registered}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-lg font-semibold">{t.label}</div>
                    <div className="text-xs font-mono text-white/40 mt-0.5">
                      {t.revenue_threshold_inr === 0
                        ? "Entry tier"
                        : `≥ ₹${(t.revenue_threshold_inr / 100000).toLocaleString()} lakh annual eligible revenue`}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {t.benefits.map((b) => (
                    <span
                      key={b}
                      className="text-[10px] font-mono bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5"
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDING 100 */}
      {meta?.founding_program && (
        <section
          data-testid="partners-founding"
          className="max-w-6xl mx-auto px-6 py-16 border-t border-white/10"
        >
          <div className="border border-[#CCFF00]/30 rounded-lg p-8 bg-gradient-to-br from-[#CCFF00]/[0.04] to-transparent">
            <div className="flex items-center gap-2 mb-2">
              <Rocket size={14} className="text-[#CCFF00]" />
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#CCFF00] font-mono">
                Limited cohort
              </div>
            </div>
            <h2 className="text-2xl sm:text-4xl font-semibold text-white tracking-tight">
              {meta.founding_program.label}
            </h2>
            <p className="mt-3 text-white/60 max-w-2xl">
              The first 100 approved partners worldwide. Time-boxed benefits,
              no equity or exclusivity, just priority alignment while we
              build the global network together.
            </p>
            <ul className="mt-5 space-y-2 max-w-2xl">
              {meta.founding_program.benefits.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2 text-sm text-white/80"
                >
                  <CheckCircle2 size={14} className="text-[#CCFF00] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/partners/apply?founding=1"
              data-testid="partners-cta-founding"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold px-5 py-2.5 text-sm"
            >
              Apply for a founding slot <ArrowUpRight size={13} />
            </Link>
          </div>
        </section>
      )}

      {/* BOOK */}
      {book.length > 0 && (
        <section
          data-testid="partners-book"
          className="max-w-6xl mx-auto px-6 py-16 border-t border-white/10"
        >
          <div className="grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
                <BookOpen size={12} /> The Partners Book
              </div>
              <h2 className="text-3xl font-semibold text-white tracking-tight">
                Five volumes. One operating system.
              </h2>
              <p className="mt-4 text-white/60 leading-relaxed">
                The commercial, operational, and governance framework that
                runs the network. Available for download — no gated form,
                no email capture.
              </p>
              <p className="mt-3 text-[11px] text-white/40 italic">
                Interactive versions move into Mirrorloops Academy in a
                later phase.
              </p>
            </div>
            <div className="lg:col-span-3 space-y-2">
              {book.map((b) => (
                <a
                  key={b.part}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/partners/book/${b.part}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`partners-book-part-${b.part}`}
                  className="flex items-center justify-between gap-3 p-4 border border-white/10 rounded hover:border-[#D4B878]/40 bg-white/[0.02] group transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded border border-white/10 bg-white/[0.04] flex items-center justify-center text-[#D4B878] font-mono text-xs">
                      {String(b.part).padStart(2, "0")}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white truncate">
                        {b.title}
                      </div>
                      <div className="text-[11px] text-white/40 font-mono">
                        PDF · Part {b.part} of 5
                      </div>
                    </div>
                  </div>
                  <Download
                    size={14}
                    className="text-white/40 group-hover:text-[#D4B878]"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section
        data-testid="partners-faqs"
        className="max-w-4xl mx-auto px-6 py-16 border-t border-white/10"
      >
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2">
            Questions
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            Answered directly.
          </h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details
              key={i}
              data-testid={`partners-faq-${i}`}
              className="group border border-white/10 rounded p-4 bg-white/[0.02] [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="cursor-pointer flex items-start justify-between gap-3">
                <span className="text-white font-medium text-sm">{f.q}</span>
                <ChevronRight
                  size={14}
                  className="text-white/40 shrink-0 mt-1 group-open:rotate-90 transition"
                />
              </summary>
              <div className="mt-3 text-sm text-white/60 leading-relaxed">
                {f.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center border-t border-white/10">
        <h2 className="text-3xl sm:text-5xl font-semibold text-white tracking-tight">
          Ready to build?
        </h2>
        <p className="mt-4 text-white/60 max-w-xl mx-auto">
          Applications are reviewed within 5 business days. We talk to
          every applicant — not just the ones we approve.
        </p>
        <Link
          to="/partners/apply"
          data-testid="partners-cta-apply-bottom"
          className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold px-6 py-3 text-sm"
        >
          Start your application <ArrowUpRight size={14} />
        </Link>
      </section>
    </PublicShell>
  );
}
